import React, { useLayoutEffect, useRef, useState } from "react";
import {
  ArrowRight,
  Clock3,
  Headphones,
  LockKeyhole,
  Mail,
  MapPin,
  Phone,
  RotateCcw,
  Send,
  ShieldCheck,
  Sparkles,
  Truck,
} from "lucide-react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useTheme } from "../contexts/ThemeContext";
import ApiURl from "../controllers/Api";

gsap.registerPlugin(ScrollTrigger);

const MAROON = "#2B0000";
const MAROON_DARK = "#190000";
const GOLD = "#D6B33F";
const GOLD_LIGHT = "#E9CD7A";
const CREAM = "#FBF6EF";
const SOFT_TEXT = "#6F625E";

const contactItems = [
  {
    icon: MapPin,
    title: "Our Store",
    lines: [
      "SL Gold Jewellery",
      "Uklana Mandi, Hisar",
      "Haryana, India",
    ],
  },
  {
    icon: Phone,
    title: "Phone",
    lines: ["+91 93101 63798", "+91 98765 43210"],
    href: "tel:+919310163798",
  },
  {
    icon: Mail,
    title: "Email",
    lines: ["support@slgold.com", "hello@slgold.com"],
    href: "mailto:support@slgold.com",
  },
  {
    icon: Clock3,
    title: "Store Timing",
    lines: [
      "Monday – Saturday: 10:00 AM – 8:00 PM",
      "Sunday: 11:00 AM – 6:00 PM",
    ],
  },
];

const benefits = [
  {
    icon: ShieldCheck,
    title: "Certified Jewellery",
    text: "100% hallmarked and certified jewellery",
  },
  {
    icon: Truck,
    title: "Free Shipping",
    text: "On orders above ₹20,000",
  },
  {
    icon: RotateCcw,
    title: "Easy Returns",
    text: "7 days easy return policy",
  },
  {
    icon: LockKeyhole,
    title: "Secure Payments",
    text: "Safe and protected payments",
  },
];

export default function Contact() {
  const { theme } = useTheme();

  const pageRef = useRef(null);
  const heroRef = useRef(null);
  const headingRef = useRef(null);
  const contactColumnRef = useRef(null);
  const formColumnRef = useRef(null);
  const mapRef = useRef(null);
  const benefitsRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      if (reduceMotion) {
        gsap.set(
          [
            heroRef.current,
            headingRef.current,
            contactColumnRef.current,
            formColumnRef.current,
            mapRef.current,
            benefitsRef.current,
          ],
          {
            opacity: 1,
            x: 0,
            y: 0,
          }
        );

        return;
      }

      gsap.fromTo(
        heroRef.current,
        {
          opacity: 0,
          y: 35,
          scale: 0.985,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 1.05,
          ease: "power3.out",
        }
      );

      gsap.fromTo(
        headingRef.current,
        {
          opacity: 0,
          x: -45,
        },
        {
          opacity: 1,
          x: 0,
          duration: 0.9,
          delay: 0.15,
          ease: "power3.out",
        }
      );

      gsap.fromTo(
        contactColumnRef.current,
        {
          opacity: 0,
          x: -60,
        },
        {
          opacity: 1,
          x: 0,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: contactColumnRef.current,
            start: "top 84%",
            toggleActions: "play none none reverse",
          },
        }
      );

      gsap.fromTo(
        formColumnRef.current,
        {
          opacity: 0,
          x: 60,
        },
        {
          opacity: 1,
          x: 0,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: formColumnRef.current,
            start: "top 84%",
            toggleActions: "play none none reverse",
          },
        }
      );

      const contactCards = gsap.utils.toArray(".contact-info-card");

      gsap.fromTo(
        contactCards,
        {
          opacity: 0,
          y: 28,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          stagger: 0.12,
          ease: "power2.out",
          scrollTrigger: {
            trigger: contactColumnRef.current,
            start: "top 78%",
            toggleActions: "play none none reverse",
          },
        }
      );

      gsap.fromTo(
        mapRef.current,
        {
          opacity: 0,
          y: 45,
          scale: 0.98,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.9,
          ease: "power3.out",
          scrollTrigger: {
            trigger: mapRef.current,
            start: "top 88%",
            toggleActions: "play none none reverse",
          },
        }
      );

      const benefitCards = gsap.utils.toArray(".contact-benefit-card");

      gsap.fromTo(
        benefitCards,
        {
          opacity: 0,
          y: 35,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          stagger: 0.1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: benefitsRef.current,
            start: "top 90%",
            toggleActions: "play none none reverse",
          },
        }
      );

      gsap.to(".contact-hero-image", {
        yPercent: 8,
        ease: "none",
        scrollTrigger: {
          trigger: heroRef.current,
          start: "top top",
          end: "bottom top",
          scrub: 1,
        },
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: value,
    }));

    if (submitted) {
      setSubmitted(false);
    }
    if (submitMessage) setSubmitMessage("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSending(true);
    setSubmitted(false);
    setSubmitMessage("");

    const payload = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      payload.append(key, value);
    });

    try {
      const response = await fetch(`${ApiURl}saveContactMessage.php`, {
        method: "POST",
        body: payload,
      });
      const data = await response.json();

      if (!data?.success) {
        throw new Error(data?.message || "Could not submit message.");
      }

      setSubmitted(true);
      setSubmitMessage(data.message || "Thank you! Your message has been submitted successfully.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      setSubmitMessage(error.message || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main
      ref={pageRef}
      className="min-h-screen overflow-hidden"
      style={{
        backgroundColor: theme?.user_bg || CREAM,
        color: theme?.user_text || MAROON_DARK,
      }}
    >
      {/* Hero section */}
      <section
        ref={heroRef}
        className="
          relative min-h-[420px] overflow-hidden
          border-b border-[#D6B33F]/20
          sm:min-h-[450px]
          lg:min-h-[320px]
        "
      >
        <div
          className="
            absolute inset-0
            lg:left-[54%]
          "
        >
          <img
            className="
              contact-hero-image h-full w-full scale-[1.08] object-cover
              object-[65%_center]
              lg:scale-105 lg:object-center
            "
            src="https://images.unsplash.com/photo-1611652022419-a9419f74343d?q=90&w=1800&auto=format&fit=crop"
            alt="Luxury gold jewellery"
          />

          <div
            className="
              absolute inset-0
              bg-gradient-to-b
              from-[#FBF6EF]/95 via-[#FBF6EF]/65 to-[#FBF6EF]/95
              lg:bg-gradient-to-r
              lg:from-[#FBF6EF] lg:via-[#FBF6EF]/30 lg:to-transparent
            "
          />

          <div className="absolute inset-0 bg-[#2B0000]/10" />
        </div>

        <div
          className="
            relative z-10 mx-auto flex min-h-[420px] max-w-[1420px]
            items-start px-5 pb-10 pt-14
            sm:min-h-[450px] sm:px-8 sm:pt-16
            lg:min-h-[320px] lg:items-center lg:px-14 lg:py-14
            xl:px-20
          "
        >
          <div
            ref={headingRef}
            className="
              max-w-[580px] rounded-[26px]
              border border-white/60 bg-[#FBF6EF]/75
              px-5 py-6 shadow-[0_24px_70px_rgba(43,0,0,0.08)]
              backdrop-blur-[5px]
              sm:px-8 sm:py-8
              lg:border-0 lg:bg-transparent lg:p-0 lg:shadow-none
              lg:backdrop-blur-none
            "
          >
            <div
              className="
                mb-4 inline-flex items-center gap-2 rounded-full
                border border-[#D6B33F]/35 bg-white/65
                px-3 py-1.5 text-[10px] font-semibold uppercase
                tracking-[0.28em] sm:text-xs
              "
              style={{ color: MAROON }}
            >
              <Sparkles size={14} style={{ color: GOLD }} />
              We are here for you
            </div>

            <h1
              className="
                font-serif text-[42px] leading-[1.02]
                sm:text-6xl lg:text-[68px]
              "
              style={{ color: MAROON_DARK }}
            >
              Get In Touch
            </h1>

            <div className="mt-5 flex items-center gap-3">
              <span className="h-px w-16" style={{ backgroundColor: GOLD }} />

              <span
                className="
                  flex h-7 w-7 rotate-45 items-center justify-center
                  rounded-[7px] border
                "
                style={{
                  borderColor: `${GOLD}80`,
                  backgroundColor: "#FFFDF9",
                }}
              >
                <span
                  className="h-2 w-2 rounded-[2px]"
                  style={{ backgroundColor: GOLD }}
                />
              </span>

              <span className="h-px w-16" style={{ backgroundColor: GOLD }} />
            </div>

            <p
              className="
                mt-5 max-w-[520px] text-sm leading-7
                sm:text-[15px] sm:leading-8
              "
              style={{ color: SOFT_TEXT }}
            >
              We would love to hear from you. Reach out to us for jewellery
              enquiries, order support, personalised assistance or feedback.
            </p>
          </div>
        </div>
      </section>

      {/* Contact content */}
      <section className="relative px-4 py-12 sm:px-8 lg:px-14 lg:py-16 xl:px-20">
        <div className="pointer-events-none absolute left-0 top-0 h-72 w-72 rounded-full bg-[#D6B33F]/[0.05] blur-3xl" />

        <div
          className="
            relative mx-auto grid max-w-[1420px] gap-12
            lg:grid-cols-[0.76fr_1.35fr] lg:gap-12
            xl:grid-cols-[420px_1fr] xl:gap-16
          "
        >
          {/* Left contact information */}
          <aside ref={contactColumnRef}>
            <div className="mb-7">
              <p
                className="
                  text-xs font-semibold uppercase tracking-[0.25em]
                "
                style={{ color: GOLD }}
              >
                Contact Details
              </p>

              <h2
                className="
                  mt-2 font-serif text-3xl sm:text-[38px]
                "
                style={{ color: MAROON_DARK }}
              >
                Contact Information
              </h2>

              <p
                className="mt-3 max-w-md text-sm leading-7"
                style={{ color: theme?.user_text_soft || SOFT_TEXT }}
              >
                Connect with our jewellery specialists for dedicated support
                and personalised guidance.
              </p>
            </div>

            <div className="space-y-3.5">
              {contactItems.map(
                ({ icon: Icon, title, lines, href }, index) => {
                  const content = (
                    <div
                      className="
                        contact-info-card group flex min-h-[105px]
                        items-start gap-4 rounded-[18px] border
                        bg-white/65 p-4
                        shadow-[0_12px_35px_rgba(43,0,0,0.045)]
                        backdrop-blur-sm
                        transition-all duration-300
                        hover:-translate-y-1
                        hover:shadow-[0_18px_45px_rgba(43,0,0,0.1)]
                        sm:p-5
                      "
                      style={{
                        borderColor: theme?.user_border || "#E8D9D0",
                      }}
                    >
                      <div
                        className="
                          flex h-12 w-12 shrink-0 items-center
                          justify-center rounded-full
                          shadow-[0_8px_20px_rgba(43,0,0,0.22)]
                          transition-transform duration-300
                          group-hover:scale-105
                        "
                        style={{
                          background: `linear-gradient(145deg, ${MAROON}, ${MAROON_DARK})`,
                          color: GOLD_LIGHT,
                        }}
                      >
                        <Icon size={20} strokeWidth={1.8} />
                      </div>

                      <div className="pt-0.5">
                        <h3
                          className="font-serif text-lg font-semibold"
                          style={{ color: MAROON_DARK }}
                        >
                          {title}
                        </h3>

                        <div
                          className="mt-1 text-[13px] leading-5.5"
                          style={{
                            color: theme?.user_text_soft || SOFT_TEXT,
                          }}
                        >
                          {lines.map((line) => (
                            <p key={line}>{line}</p>
                          ))}
                        </div>
                      </div>

                      <span
                        className="
                          ml-auto mt-2 flex h-7 w-7 shrink-0
                          items-center justify-center rounded-full
                          opacity-0 transition-all duration-300
                          group-hover:translate-x-1 group-hover:opacity-100
                        "
                        style={{
                          backgroundColor: `${GOLD}20`,
                          color: MAROON,
                        }}
                      >
                        <ArrowRight size={14} />
                      </span>
                    </div>
                  );

                  return href ? (
                    <a
                      key={title}
                      href={href}
                      className="block"
                      aria-label={title}
                    >
                      {content}
                    </a>
                  ) : (
                    <React.Fragment key={title}>{content}</React.Fragment>
                  );
                }
              )}
            </div>

            <a
              href="tel:+919310163798"
              className="
                group mt-4 flex items-center justify-between
                rounded-[18px] px-5 py-4
                shadow-[0_18px_45px_rgba(43,0,0,0.2)]
                transition-transform duration-300 hover:-translate-y-1
              "
              style={{
                background: `linear-gradient(110deg, ${MAROON_DARK}, ${MAROON})`,
                color: "#FFFFFF",
              }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="
                    flex h-10 w-10 items-center justify-center
                    rounded-full border
                  "
                  style={{
                    borderColor: `${GOLD}70`,
                    color: GOLD_LIGHT,
                  }}
                >
                  <Headphones size={18} />
                </div>

                <div>
                  <p className="text-sm font-semibold">
                    Need Immediate Help?
                  </p>
                  <p className="mt-0.5 text-[11px] text-white/65">
                    Speak with our support team
                  </p>
                </div>
              </div>

              <div
                className="
                  flex items-center gap-2 text-xs font-semibold
                  transition-transform duration-300
                  group-hover:translate-x-1
                "
                style={{ color: GOLD_LIGHT }}
              >
                Chat Now
                <ArrowRight size={15} />
              </div>
            </a>
          </aside>

          {/* Right contact form */}
          <div ref={formColumnRef}>
            <div className="mb-7">
              <p
                className="text-xs font-semibold uppercase tracking-[0.25em]"
                style={{ color: GOLD }}
              >
                Send Your Enquiry
              </p>

              <h2
                className="mt-2 font-serif text-3xl sm:text-[38px]"
                style={{ color: MAROON_DARK }}
              >
                Send Us a Message
              </h2>

              <p
                className="mt-3 max-w-2xl text-sm leading-7"
                style={{ color: theme?.user_text_soft || SOFT_TEXT }}
              >
                Fill in the form and our team will connect with you as soon as
                possible.
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="
                rounded-[24px] border bg-white/55 p-4
                shadow-[0_22px_70px_rgba(43,0,0,0.06)]
                backdrop-blur-sm
                sm:p-6 lg:p-7
              "
              style={{
                borderColor: theme?.user_border || "#E8D9D0",
              }}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <InputField
                  name="name"
                  type="text"
                  placeholder="Your Name *"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />

                <InputField
                  name="email"
                  type="email"
                  placeholder="Your Email *"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />

                <InputField
                  name="phone"
                  type="tel"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={handleChange}
                />

                <InputField
                  name="subject"
                  type="text"
                  placeholder="Subject"
                  value={formData.subject}
                  onChange={handleChange}
                />
              </div>

              <div className="mt-4">
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={7}
                  placeholder="Write your message here..."
                  className="
                    w-full resize-none rounded-[14px] border
                    bg-[#FFFDF9]/80 px-4 py-4 text-sm
                    outline-none transition-all duration-300
                    placeholder:text-[#8B7F79]
                    focus:-translate-y-0.5
                    focus:shadow-[0_12px_35px_rgba(43,0,0,0.08)]
                  "
                  style={{
                    borderColor: theme?.user_border || "#E7DCD4",
                    color: theme?.user_text || MAROON_DARK,
                  }}
                  onFocus={(event) => {
                    event.currentTarget.style.borderColor = GOLD;
                  }}
                  onBlur={(event) => {
                    event.currentTarget.style.borderColor =
                      theme?.user_border || "#E7DCD4";
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={sending}
                className="
                  group mt-4 flex w-full items-center justify-center
                  gap-3 rounded-[14px] px-5 py-4
                  text-sm font-semibold text-white
                  shadow-[0_15px_35px_rgba(43,0,0,0.22)]
                  transition-all duration-300
                  hover:-translate-y-1
                  hover:shadow-[0_20px_45px_rgba(43,0,0,0.3)]
                  active:translate-y-0
                  disabled:cursor-not-allowed disabled:opacity-70
                "
                style={{
                  background: `linear-gradient(100deg, ${MAROON_DARK}, ${MAROON})`,
                }}
              >
                <Send
                  size={17}
                  style={{ color: GOLD_LIGHT }}
                  className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                />

                {sending ? "Sending..." : "Send Message"}

                <ArrowRight
                  size={17}
                  style={{ color: GOLD_LIGHT }}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </button>

              {submitMessage && (
                <div
                  className="
                    mt-4 rounded-xl border px-4 py-3
                    text-center text-sm font-medium
                  "
                  style={{
                    borderColor: `${GOLD}80`,
                    backgroundColor: submitted ? `${GOLD}12` : "#fee2e2",
                    color: submitted ? MAROON : "#991b1b",
                  }}
                >
                  {submitMessage}
                </div>
              )}
            </form>

            {/* Map */}
            <div
              ref={mapRef}
              className="
                relative mt-5 min-h-[240px] overflow-hidden
                rounded-[24px] border
                shadow-[0_20px_55px_rgba(43,0,0,0.08)]
                sm:min-h-[300px]
              "
              style={{
                borderColor: theme?.user_border || "#E8D9D0",
                backgroundColor: "#EDE7D9",
              }}
            >
              <iframe
                title="SL Gold Store Location"
                src="https://www.google.com/maps?q=Uklana%20Mandi%20Hisar%20Haryana&output=embed"
                className="absolute inset-0 h-full w-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />

              <div
                className="
                  pointer-events-none absolute inset-x-0 top-0
                  h-24 bg-gradient-to-b from-[#FBF6EF]/60 to-transparent
                "
              />

              <div
                className="
                  absolute bottom-4 left-4 right-4
                  flex items-center justify-between gap-3
                  rounded-[16px] border border-white/70
                  bg-white/85 px-4 py-3
                  shadow-[0_12px_30px_rgba(43,0,0,0.12)]
                  backdrop-blur-md
                  sm:left-5 sm:right-auto sm:max-w-sm
                "
              >
                <div className="flex items-center gap-3">
                  <div
                    className="
                      flex h-10 w-10 shrink-0 items-center
                      justify-center rounded-full
                    "
                    style={{
                      backgroundColor: MAROON,
                      color: GOLD_LIGHT,
                    }}
                  >
                    <MapPin size={18} />
                  </div>

                  <div>
                    <p
                      className="font-serif text-base font-semibold"
                      style={{ color: MAROON_DARK }}
                    >
                      SL Gold Jewellery
                    </p>

                    <p
                      className="mt-0.5 text-[11px]"
                      style={{ color: SOFT_TEXT }}
                    >
                      Uklana Mandi, Hisar, Haryana
                    </p>
                  </div>
                </div>

                <ArrowRight
                  size={17}
                  className="shrink-0"
                  style={{ color: GOLD }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits section */}
      <section
        ref={benefitsRef}
        className="
          border-y px-4 py-8 sm:px-8 lg:px-14 lg:py-10 xl:px-20
        "
        style={{
          borderColor: theme?.user_border || "#E8D9D0",
          backgroundColor: "rgba(255, 253, 249, 0.65)",
        }}
      >
        <div
          className="
            mx-auto grid max-w-[1420px] grid-cols-2
            gap-x-3 gap-y-6
            lg:grid-cols-4 lg:gap-0
          "
        >
          {benefits.map(({ icon: Icon, title, text }, index) => (
            <div
              key={title}
              className={`
                contact-benefit-card group relative
                flex flex-col items-center px-2 text-center
                sm:px-5
                lg:flex-row lg:items-center lg:gap-4 lg:text-left
                lg:px-8
                ${
                  index !== benefits.length - 1
                    ? "lg:border-r lg:border-[#2B0000]/10"
                    : ""
                }
              `}
            >
              <div
                className="
                  flex h-12 w-12 shrink-0 items-center justify-center
                  rounded-full border transition-all duration-300
                  group-hover:-translate-y-1
                  sm:h-14 sm:w-14
                "
                style={{
                  borderColor: `${GOLD}65`,
                  backgroundColor: `${GOLD}0D`,
                  color: MAROON,
                }}
              >
                <Icon size={23} strokeWidth={1.5} />
              </div>

              <div className="mt-3 lg:mt-0">
                <h3
                  className="font-serif text-sm font-semibold sm:text-base"
                  style={{ color: MAROON_DARK }}
                >
                  {title}
                </h3>

                <p
                  className="
                    mx-auto mt-1 max-w-[150px] text-[10px] leading-4
                    sm:text-xs sm:leading-5 lg:mx-0 lg:max-w-[180px]
                  "
                  style={{ color: theme?.user_text_soft || SOFT_TEXT }}
                >
                  {text}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}

function InputField({
  name,
  type,
  placeholder,
  value,
  onChange,
  required = false,
}) {
  const { theme } = useTheme();

  return (
    <input
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      className="
        h-13 w-full rounded-[14px] border
        bg-[#FFFDF9]/80 px-4 text-sm
        outline-none transition-all duration-300
        placeholder:text-[#8B7F79]
        focus:-translate-y-0.5
        focus:shadow-[0_12px_35px_rgba(43,0,0,0.08)]
      "
      style={{
        height: "52px",
        borderColor: theme?.user_border || "#E7DCD4",
        color: theme?.user_text || MAROON_DARK,
      }}
      onFocus={(event) => {
        event.currentTarget.style.borderColor = GOLD;
      }}
      onBlur={(event) => {
        event.currentTarget.style.borderColor =
          theme?.user_border || "#E7DCD4";
      }}
    />
  );
}
